import { TokenInfo } from "@/models/response/auth";
import getToken, { setToken } from "@/utilities/helper/getToken";
import axios, { isAxiosError } from "axios";
import { jwtDecode } from "jwt-decode";

// Access tokens live 180 minutes and every refresh token works exactly once
// (the server deletes it when it issues the next one). iOS home-screen apps are
// suspended in the background and often come back before the network does, so
// a refresh that fails for a network reason must never end the session.

export const REFRESH_TOKEN_PATH = "/api/Authenticate/RefreshToken";

// Refresh a minute early so a request never leaves with a token that expires in flight.
const EXPIRY_MARGIN_MS = 60_000;
const REFRESH_ATTEMPTS = 3;
const REFRESH_TIMEOUT_MS = 15_000;
const REFRESH_LOCK = "padix-token-refresh";

const apiBase = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/?$/, "/");

const expiresAt = (accessToken?: string | null) => {
  if (!accessToken) return 0;
  try {
    return (jwtDecode<{ exp?: number }>(accessToken).exp ?? 0) * 1000;
  } catch {
    return 0;
  }
};

const isFresh = (token: TokenInfo | null, margin = EXPIRY_MARGIN_MS) =>
  expiresAt(token?.accessToken) - margin > Date.now();

export const hasSession = () => Boolean(getToken()?.accessToken);

// No response, a timeout, throttling or a server error says nothing about the session.
export const isTransientError = (error: unknown) =>
  isAxiosError(error) &&
  (!error.response ||
    error.response.status >= 500 ||
    error.response.status === 408 ||
    error.response.status === 429);

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Exchanges the stored refresh token for a new access token.
 * Resolves to null when the session is really gone; throws only on transient failures.
 */
const rotate = async (rejectedToken?: string): Promise<string | null> => {
  const current = getToken();
  if (!current?.refreshTokenId) return null;
  // Another tab (or an earlier call) already replaced the token we were about to refresh.
  if (current.accessToken !== rejectedToken && isFresh(current)) return current.accessToken;

  for (let attempt = 1; ; attempt++) {
    try {
      const { data } = await axios.post<Partial<TokenInfo>>(
        `${apiBase}${REFRESH_TOKEN_PATH.slice(1)}`,
        { refreshTokenId: current.refreshTokenId },
        { timeout: REFRESH_TIMEOUT_MS },
      );
      if (!data?.accessToken) return null;
      setToken({
        accessToken: data.accessToken,
        expirationDate: data.expirationDate ?? "",
        roles: data.roles ?? [],
        refreshTokenId: data.refreshTokenId,
      });
      return data.accessToken;
    } catch (error) {
      if (!isTransientError(error)) {
        // A tab without the lock may have rotated the token first; use its result.
        const latest = getToken();
        return latest && latest.refreshTokenId !== current.refreshTokenId && isFresh(latest, 0)
          ? latest.accessToken
          : null;
      }
      if (attempt >= REFRESH_ATTEMPTS) throw error;
      await delay(attempt * 1000);
    }
  }
};

// Serialise refreshes across tabs too: two tabs spending the same one-time refresh token
// would log one of them out.
const withLock = <T>(task: () => Promise<T>): Promise<T> =>
  navigator.locks?.request
    ? (navigator.locks.request(REFRESH_LOCK, task) as Promise<T>)
    : task();

let inFlight: Promise<string | null> | null = null;

/** One refresh at a time; concurrent callers share its result. */
export const refreshSession = (rejectedToken?: string) => {
  inFlight ??= withLock(() => rotate(rejectedToken)).finally(() => {
    inFlight = null;
  });
  return inFlight;
};

let ending = false;

/** Ends a session the server no longer accepts. Only the token is removed; other preferences stay. */
export const endSession = () => {
  if (ending) return;
  ending = true;
  setToken(null);
  const platform = localStorage.getItem("platform");
  window.location.replace(platform ? "/auth/login?platform=android" : "/auth/login");
};

/**
 * The access token to send now, refreshed first when it is about to expire.
 * Throws the network error when the token has already expired and cannot be refreshed yet;
 * the session is kept for the next attempt.
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  const token = getToken();
  if (!token?.accessToken) return null;
  if (isFresh(token) || !token.refreshTokenId) return token.accessToken;
  try {
    const accessToken = await refreshSession(token.accessToken);
    if (!accessToken) endSession();
    return accessToken;
  } catch (error) {
    // Still valid for a little while: send it rather than fail.
    if (isFresh(token, 0)) return token.accessToken;
    throw error;
  }
};

/** Refreshes as soon as the app comes back to the foreground or online, not on the first failing request. */
export const watchSessionResume = () => {
  const check = () => {
    if (document.visibilityState === "visible") getValidAccessToken().catch(() => undefined);
  };
  document.addEventListener("visibilitychange", check);
  window.addEventListener("pageshow", check);
  window.addEventListener("online", check);
};
